import { useState, type ReactNode } from 'react'
import { Badge, Field, KgInput } from '@/ui'
import { useI18n } from '@/i18n'
import type { Grams } from '@/lib/weight'
import type { SyncMark } from '../projection'

export { ScanButton, Counter, YesNo } from '@/features/store/components/bits'

export interface Reading {
  read_g: Grams
  confirmed_twice: boolean
}

/**
 * A scale reading in kg. A reading outside the usual range (`needsTwice`) must be typed a second
 * time and match before it counts (measurements rule). Reports null until it is good.
 */
export function ReadingField({
  label,
  hint,
  needsTwice,
  onChange,
  error,
}: {
  label: ReactNode
  hint?: ReactNode
  needsTwice: (g: Grams) => boolean
  onChange: (r: Reading | null) => void
  error?: ReactNode
}) {
  const { t } = useI18n()
  const [first, setFirst] = useState<Grams | null>(null)
  const [second, setSecond] = useState<Grams | null>(null)
  const twice = first !== null && first > 0 && needsTwice(first)
  const report = (a: Grams | null, b: Grams | null) => {
    if (a === null || a <= 0) return onChange(null)
    if (!needsTwice(a)) return onChange({ read_g: a, confirmed_twice: false })
    onChange(b === a ? { read_g: a, confirmed_twice: true } : null)
  }
  return (
    <div className="flex flex-col gap-3">
      <Field label={label} hint={hint} required error={error}>
        <KgInput
          value={first}
          onChange={(g) => {
            setFirst(g)
            report(g, second)
          }}
        />
      </Field>
      {twice && (
        <Field
          label={t('process.typeAgain')}
          hint={t('process.typeAgainHint')}
          required
          error={second !== null && second !== first ? t('process.typeAgainMismatch') : undefined}
        >
          <KgInput
            value={second}
            onChange={(g) => {
              setSecond(g)
              report(first, g)
            }}
          />
        </Field>
      )}
    </div>
  )
}

/** Not synced / held at the office / sync problem. */
export function SyncBadges({ sync }: { sync: SyncMark }) {
  const { t } = useI18n()
  return (
    <>
      {sync.pending_command_id && <Badge tone="info">{t('process.badge.notSynced')}</Badge>}
      {sync.held && <Badge tone="warn">{t('process.badge.heldAtOffice')}</Badge>}
      {sync.error && <Badge tone="danger">{t('process.badge.syncProblem')}</Badge>}
    </>
  )
}

/** Brand / ordinary badge for a stream. */
export function StreamBadge({ stream }: { stream: 'MAIN' | 'CONV' }) {
  const { t } = useI18n()
  return stream === 'MAIN' ? (
    <Badge tone="ok">{t('process.stream.MAIN')}</Badge>
  ) : (
    <Badge tone="danger">{t('process.stream.CONV')}</Badge>
  )
}

/** One label / value line of a summary table. */
export function Line({
  label,
  value,
  strong,
  tone,
}: {
  label: ReactNode
  value: ReactNode
  strong?: boolean
  tone?: 'ok' | 'warn' | 'danger'
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-rule/60 py-2 last:border-b-0">
      <span className="text-[14px] text-muted">{label}</span>
      <span
        className={[
          'text-right font-mono tabular',
          strong ? 'text-[17px] font-bold' : 'text-[15px]',
          tone === 'ok' ? 'text-ok' : tone === 'warn' ? 'text-warn' : tone === 'danger' ? 'text-danger' : '',
        ].join(' ')}
      >
        {value}
      </span>
    </div>
  )
}

